import React, { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Skeleton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material'
import EmailList from './EmailList'
import EmailDetail from './EmailDetail'
import {
  getEmailsListRoutine,
  getEmailDetailsRoutine,
  markEmailAsReadRoutine,
  refreshInboxRoutine
} from '../../../features/inbox/ducks/actions'
import {
  getEmails,
  getEmailsLoading,
  getEmailsError,
  getInboxStats,
  getHasNextPage
} from '../../../features/inbox/ducks/selectors'
import { getSelectedBp } from '../../../features/bpDetails/ducks/selectors'
import { hideEmail } from 'utils/user'

const InboxTab = () => {
  const { id: buildingPassportId } = useParams()
  const dispatch = useDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [selectedEmail, setSelectedEmail] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('time')
  const [filterSender, setFilterSender] = useState('all')
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [locallyReadEmails, setLocallyReadEmails] = useState(new Set())

  const emails = useSelector(getEmails)
  const loading = useSelector(getEmailsLoading)
  const error = useSelector(getEmailsError)
  const stats = useSelector(getInboxStats)
  const hasNextPage = useSelector(getHasNextPage)
  const buildingPassport = useSelector(getSelectedBp)

  // Filter and sort emails
  const filteredAndSortedEmails = useMemo(() => {
    let filtered = emails.filter(email => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        const matchesSearch =
          email.subject?.toLowerCase().includes(searchLower) ||
          email.fromEmail?.toLowerCase().includes(searchLower) ||
          email.fromName?.toLowerCase().includes(searchLower) ||
          email.bodyText?.toLowerCase().includes(searchLower)

        if (!matchesSearch) return false
      }

      // Sender filter
      if (filterSender !== 'all') {
        if (filterSender === 'internal' && !email.isFromRegisteredUser) return false
        if (filterSender === 'external' && email.isFromRegisteredUser) return false
        if (filterSender !== 'internal' && filterSender !== 'external' && email.fromEmail !== filterSender) return false
      }

      return true
    })

    // Sort emails
    if (sortBy === 'time') {
      filtered = filtered.sort((a, b) => new Date(b.receivedAt) - new Date(a.receivedAt))
    } else if (sortBy === 'sender') {
      filtered = filtered.sort((a, b) => {
        const senderA = a.fromName || a.fromEmail || ''
        const senderB = b.fromName || b.fromEmail || ''
        return senderA.localeCompare(senderB)
      })
    }

    return filtered
  }, [emails, searchTerm, filterSender, sortBy])

  // Get unique senders for filter dropdown
  const uniqueSenders = useMemo(() => {
    const senders = new Set()
    emails.forEach(email => { if (email.fromEmail) senders.add(email.fromEmail) })
    return Array.from(senders).sort()
  }, [emails])

  // Effects
  useEffect(() => {
    if (buildingPassportId) {
      dispatch(refreshInboxRoutine.trigger({ bpId: buildingPassportId }))
    }
  }, [dispatch, buildingPassportId])

  // Update selected email when emails list changes (after mark as read)
  useEffect(() => {
    if (selectedEmail && emails.length > 0) {
      const updatedEmail = emails.find(email => email.id === selectedEmail.id)
      if (updatedEmail && updatedEmail.isReadByMe !== selectedEmail.isReadByMe) {
        setSelectedEmail(updatedEmail)
      }
    }
  }, [emails, selectedEmail])

  // Handlers
  const handleEmailSelect = async email => {
    setSelectedEmail(email)

    // Fetch full email details for attachments and other detailed info
    if (!email.isReadByMe) {
      setLocallyReadEmails(prev => new Set(prev).add(email.id))
      dispatch(markEmailAsReadRoutine.trigger({ bpId: buildingPassportId, emailId: email.id }))
    }

    dispatch(getEmailDetailsRoutine.trigger({ bpId: buildingPassportId, emailId: email.id }))
  }

  const handleBackToList = () => setSelectedEmail(null)

  const handleLoadMore = () => {
    const nextPage = currentPage + 1
    setCurrentPage(nextPage)
    dispatch(getEmailsListRoutine.trigger({ bpId: buildingPassportId, page: nextPage, append: true }))
  }

  const handleCopyEmail = () => {
    if (buildingPassport.inboxEmailDisplay) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(buildingPassport.inboxEmailDisplay)
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = buildingPassport.inboxEmailDisplay
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
    }
  }

  const handleRefresh = () => {
    setCurrentPage(1)
    dispatch(refreshInboxRoutine.trigger({ bpId: buildingPassportId }))
  }

  const handleRetrySuccess = () => {
    dispatch(refreshInboxRoutine.trigger({ bpId: buildingPassportId }))
  }

  if (loading && !emails.length) {
    return (
      <Box sx={{ height: '80vh', bgcolor: '#f7f8fd', p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' height='400px'>
        <Typography variant='h6' color='error'>Error: {error.message}</Typography>
      </Box>
    )
  }

  if (isMobile) {
    if (selectedEmail) {
      return (
        <Box sx={{ height: 'calc(100vh - 80px)', bgcolor: 'white', display: 'flex', flexDirection: 'column' }}>
          <EmailDetail
            email={selectedEmail}
            buildingPassportId={buildingPassportId}
            onRetrySuccess={handleRetrySuccess}
            onBack={handleBackToList}
          />
        </Box>
      )
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)', bgcolor: 'white' }}>

        <Box data-tour-id='inbox.header' sx={{ p: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>Inbox</Typography>
            <Typography variant="caption" color="text.secondary">
              {stats?.unreadEmails || 0} unread
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={() => setShowMobileFilters(!showMobileFilters)} size="small" sx={{ mr: 1 }}>
              <FilterListIcon fontSize="small" color={showMobileFilters ? "primary" : "action"} />
            </IconButton>
            <IconButton onClick={handleRefresh} size="small" disabled={loading}>
              {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>

        {showMobileFilters && (
          <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
            {/* Search - Alb pe fundal gri */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ bgcolor: 'white' }}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>)
              }}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort by"
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ bgcolor: 'white' }}
                >
                  <MenuItem value="time">Time (Newest)</MenuItem>
                  <MenuItem value="sender">Sender (A-Z)</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Filter Sender</InputLabel>
                <Select
                  value={filterSender}
                  label="Filter Sender"
                  onChange={(e) => setFilterSender(e.target.value)}
                  sx={{ bgcolor: 'white' }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="internal">Internal</MenuItem>
                  <MenuItem value="external">External</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <EmailList
            emails={filteredAndSortedEmails}
            selectedEmailId={null}
            onEmailSelect={handleEmailSelect}
            onLoadMore={handleLoadMore}
            hasNextPage={hasNextPage}
            loading={loading}
            isMobile={true}
            locallyReadEmails={locallyReadEmails}
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column', bgcolor: '#f7f8fd' }}>
      <Paper data-tour-id='inbox.header' elevation={1} sx={{ mb: 2, borderRadius: 1, boxShadow: '0 4px 9px rgba(0, 0, 0, 0.02)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Box>
            <Typography variant='h5' sx={{ fontWeight: 600, color: '#D24724', mb: 0.5, fontFamily: 'FuturaPT-Book, Open Sans, sans-serif' }}>
              Inbox
            </Typography>
            {stats && (
              <Typography variant='body2' color='text.secondary'>
                {stats.totalEmails || 0} total • {stats.unreadEmails || 0} unread
                {buildingPassport.inboxEmailDisplay && (
                  <>
                    {' '} • <strong>Email to:</strong>{' '}
                    <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main', fontFamily: 'monospace' }}>
                      {buildingPassport.inboxEmailDisplay}
                    </Box>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={handleCopyEmail} sx={{ ml: 0.5, p: 0.25 }}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Typography>
            )}
          </Box>
          <Tooltip title='Refresh inbox'>
            <IconButton onClick={handleRefresh} disabled={loading} sx={{ bgcolor: '#D24724', color: 'white', '&:hover': { bgcolor: '#b13e1f' } }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <Paper elevation={1} sx={{ mb: 2, p: 2, borderRadius: 1, boxShadow: '0 4px 9px rgba(0, 0, 0, 0.02)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" placeholder="Search emails..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: '#84889C' }} /></InputAdornment>),
                endAdornment: searchTerm && (<InputAdornment position="end"><IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon sx={{ color: '#84889C' }} /></IconButton></InputAdornment>)
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort by</InputLabel>
              <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sort by">
                <MenuItem value="time">Time (Newest)</MenuItem>
                <MenuItem value="sender">Sender (A-Z)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by sender</InputLabel>
              <Select value={filterSender} onChange={(e) => setFilterSender(e.target.value)} label="Filter by sender">
                <MenuItem value="all">All senders</MenuItem>
                <MenuItem value="internal">Building Passport Users only</MenuItem>
                <MenuItem value="external">External only</MenuItem>
                {uniqueSenders.map(sender => (<MenuItem key={sender} value={sender}>{hideEmail(sender)}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ flexGrow: 1, height: 'calc(100% - 160px)' }}>
        <Grid item xs={12} md={5} lg={4} sx={{ height: '100%' }}>
          <Paper elevation={1} sx={{ height: '100%', borderRadius: 1, boxShadow: '0 4px 9px rgba(0, 0, 0, 0.02)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e6e6e6', bgcolor: '#fafbfc' }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600, fontFamily: 'FuturaPT-Book, Open Sans, sans-serif' }}>
                Messages ({filteredAndSortedEmails.length})
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <EmailList
                emails={filteredAndSortedEmails}
                selectedEmailId={selectedEmail?.id}
                onEmailSelect={handleEmailSelect}
                onLoadMore={handleLoadMore}
                hasNextPage={hasNextPage}
                loading={loading}
                locallyReadEmails={locallyReadEmails}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Email Detail - Right Panel */}
        <Grid item xs={12} md={7} lg={8} sx={{ height: '100%' }}>
          <Paper elevation={1} sx={{ height: '100%', borderRadius: 1, boxShadow: '0 4px 9px rgba(0, 0, 0, 0.02)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <EmailDetail
              email={selectedEmail}
              buildingPassportId={buildingPassportId}
              onRetrySuccess={handleRetrySuccess}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default InboxTab
